import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Card, Typography, Box } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { motion } from 'framer-motion'

const EXPANDED_CARD_THEMES = {
  soft_cream: {
    frontFrom: '#fffaf0',
    frontTo: '#f9f2e6',
    backFrom: '#fffaf0',
    backTo: '#f8f1e4',
    radialAlpha: '22',
  },
  warm_sand: {
    frontFrom: '#fdf7ef',
    frontTo: '#f5e8d8',
    backFrom: '#fdf7ef',
    backTo: '#f3e3d1',
    radialAlpha: '20',
  },
  pearl_gray: {
    frontFrom: '#f8f9fb',
    frontTo: '#edf1f6',
    backFrom: '#f8f9fb',
    backTo: '#e9eef4',
    radialAlpha: '1c',
  },
  pearl_mist: {
    frontFrom: '#ffffff',
    frontTo: '#fdfefe',
    backFrom: '#ffffff',
    backTo: '#fcfefe',
    radialAlpha: '22',
    useCardTint: true,
    tintAlphaFront: '0f',
    tintAlphaBack: '0c',
  },
  silver_cloud: {
    frontFrom: '#f6f7f9',
    frontTo: '#e9edf2',
    backFrom: '#f6f7f9',
    backTo: '#e6ebf1',
    radialAlpha: '19',
  },
  dove_gray: {
    frontFrom: '#f5f7fa',
    frontTo: '#e8ecf2',
    backFrom: '#f5f7fa',
    backTo: '#e4e9f0',
    radialAlpha: '1b',
  },
  slate_frost: {
    frontFrom: '#f4f6f9',
    frontTo: '#e6ebf2',
    backFrom: '#f4f6f9',
    backTo: '#e2e8f1',
    radialAlpha: '1d',
  },
  mint_fog: {
    frontFrom: '#f4fbf7',
    frontTo: '#e7f4ed',
    backFrom: '#f4fbf7',
    backTo: '#e2f1e9',
    radialAlpha: '1d',
  },
  ivory_stone: {
    frontFrom: '#fcfaf6',
    frontTo: '#f2ede3',
    backFrom: '#fcfaf6',
    backTo: '#efe8dc',
    radialAlpha: '21',
  },
}

const ACTIVE_EXPANDED_THEME = 'pearl_mist'

const ItemRow = ({ item, cardColor }) => (
  <Box
    display="flex"
    alignItems="flex-start"
    gap={{ xs: 0.85, sm: 1, lg: 1.15 }}
    sx={{ py: { xs: 0.1, lg: 0.18 } }}>
    <Box
      component="span"
      sx={{
        display: 'flex',
        flexShrink: 0,
        color: cardColor,
        mt: { xs: '2px', md: '3px', lg: '4px' },
      }}>
      <CheckCircleIcon
        sx={{ fontSize: { xs: 14.5, sm: 15, md: 16, lg: 17, xl: 17.5 } }}
      />
    </Box>
    <Typography
      lang="es"
      variant="body2"
      color="text.secondary"
      sx={{
        fontSize: {
          xs: '0.80rem',
          sm: '0.82rem',
          md: '0.85rem',
          lg: '0.88rem',
          xl: '0.92rem',
        },
        lineHeight: { xs: 1.38, md: 1.42, lg: 1.45 },
        fontWeight: 500,
        hyphens: 'auto',
        WebkitHyphens: 'auto',
        MozHyphens: 'auto',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
        display: 'block',
      }}>
      {item.text}
    </Typography>
  </Box>
)

const ServiceCard = ({ icon, title, description, items, color, expanded, onToggle }) => {
  const [clickPulse, setClickPulse] = useState(false)
  const [showInlineHeader, setShowInlineHeader] = useState(false)
  const [twoColRows, setTwoColRows] = useState(0)
  const pulseTimeoutRef = useRef(null)
  const frontFaceRef = useRef(null)
  const titleMeasureRef = useRef(null)
  const itemsContainerRef = useRef(null)
  const cardColor = color || '#1976d2'
  const expandedTheme =
    EXPANDED_CARD_THEMES[ACTIVE_EXPANDED_THEME] || EXPANDED_CARD_THEMES.soft_cream
  const isFlipped = expanded
  const titleSx = {
    color: cardColor,
    fontWeight: 700,
    lineHeight: 1.25,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    hyphens: 'auto',
  }

  const handleCardToggle = () => {
    if (pulseTimeoutRef.current) {
      clearTimeout(pulseTimeoutRef.current)
    }
    setClickPulse(true)
    pulseTimeoutRef.current = setTimeout(() => setClickPulse(false), 90)
    onToggle()
  }

  useLayoutEffect(() => {
    if (!isFlipped) {
      setTwoColRows(0)
      return
    }
    const container = itemsContainerRef.current
    if (!container) return
    if (container.scrollHeight > container.clientHeight) {
      const maxRows = Math.floor((items?.length ?? 0) / 2)
      setTwoColRows(prev => (prev < maxRows ? prev + 1 : prev))
    }
  }, [isFlipped, twoColRows, items?.length])

  const renderItems = () => {
    if (!items?.length) return null
    const twoColItemCount = twoColRows * 2
    const result = []
    let i = 0
    while (i < items.length) {
      if (i < twoColItemCount && i + 1 < items.length) {
        const rowIdx = i
        result.push(
          <Box
            key={`row-${rowIdx}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: { xs: 0.85, md: 1, lg: 1.15 },
            }}>
            {[items[rowIdx], items[rowIdx + 1]].map((item, offset) => (
              <motion.div
                key={`${title}-item-${rowIdx + offset}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (rowIdx + offset) * 0.03 }}>
                <ItemRow item={item} cardColor={cardColor} />
              </motion.div>
            ))}
          </Box>,
        )
        i += 2
      } else {
        result.push(
          <motion.div
            key={`${title}-item-${i}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}>
            <ItemRow item={items[i]} cardColor={cardColor} />
          </motion.div>,
        )
        i++
      }
    }
    return result
  }

  useEffect(
    () => () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    const computeHeaderLayout = () => {
      if (!frontFaceRef.current || !titleMeasureRef.current) return

      const frontWidth = frontFaceRef.current.getBoundingClientRect().width
      const titleWidth = titleMeasureRef.current.getBoundingClientRect().width
      const iconWidth = 64
      const gap = 12
      const availableWidth = frontWidth - 56

      setShowInlineHeader(titleWidth + iconWidth + gap <= availableWidth)
    }

    computeHeaderLayout()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => computeHeaderLayout())
      if (frontFaceRef.current) {
        observer.observe(frontFaceRef.current)
      }
      return () => observer.disconnect()
    }

    window.addEventListener('resize', computeHeaderLayout)
    return () => window.removeEventListener('resize', computeHeaderLayout)
  }, [title])

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={expanded}
      onClick={handleCardToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleCardToggle()
        }
      }}
      elevation={0}
      sx={{
        width: '100%',
        position: 'relative',
        borderRadius: 4,
        height: { xs: 340, sm: 360, md: 390, lg: 410, xl: 430 },
        perspective: '1400px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        overflow: 'visible',
        boxShadow: 'none',
        '&:focus-visible': {
          outline: `3px solid ${cardColor}55`,
          outlineOffset: 2,
        },
      }}>
      <motion.div
        animate={{
          rotateY: isFlipped ? 180 : 0,
          scale: clickPulse ? [1, 1.045, 1] : 1,
        }}
        transition={{
          rotateY: { duration: 0.58, ease: [0.2, 0.8, 0.2, 1] },
          scale: { duration: 0.1, ease: 'easeOut' },
        }}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}>
        <Box
          ref={frontFaceRef}
          sx={{
            position: 'absolute',
            inset: 0,
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 4,
            border: `1px solid ${expanded ? `${cardColor}70` : `${cardColor}33`}`,
            borderTop: `5px solid ${cardColor}`,
            boxShadow: expanded
              ? `0 18px 40px ${cardColor}33`
              : '0 12px 28px rgba(12, 35, 66, 0.14)',
            backgroundColor: expanded ? expandedTheme.frontFrom : '#ffffff',
            background: expanded
              ? expandedTheme.useCardTint
                ? `radial-gradient(circle at 100% -10%, ${cardColor}${expandedTheme.radialAlpha} 0%, transparent 40%), linear-gradient(160deg, ${expandedTheme.frontFrom} 0%, ${cardColor}${expandedTheme.tintAlphaFront || '12'} 75%)`
                : `radial-gradient(circle at 100% -10%, ${cardColor}${expandedTheme.radialAlpha} 0%, transparent 40%), linear-gradient(160deg, ${expandedTheme.frontFrom} 0%, ${expandedTheme.frontTo} 65%)`
              : `radial-gradient(circle at 100% -10%, ${cardColor}2e 0%, transparent 42%), linear-gradient(160deg, #ffffff 0%, #f7fafc 60%)`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1.5,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              flexDirection: showInlineHeader ? 'row' : 'column',
            }}>
            <Box
              sx={{
                color: cardColor,
                width: 64,
                height: 64,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${cardColor}14`,
                boxShadow: `0 10px 20px ${cardColor}2b`,
                flexShrink: 0,
              }}>
              {icon}
            </Box>

            <Typography
              variant="h6"
              sx={{
                ...titleSx,
                textAlign: showInlineHeader ? 'left' : 'center',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}>
              {title}
            </Typography>
          </Box>

          <Typography
            ref={titleMeasureRef}
            variant="h6"
            sx={{
              ...titleSx,
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}>
            {title}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.6,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}>
            {description}
          </Typography>

          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              alignItems: 'center',
              pointerEvents: 'none',
            }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: cardColor,
              }}
            />
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                border: `1.5px solid ${cardColor}80`,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            p: { xs: 2.25, md: 2.75 },
            pb: '44px',
            borderRadius: 4,
            border: `1px solid ${expanded ? `${cardColor}70` : `${cardColor}33`}`,
            borderTop: `5px solid ${cardColor}`,
            boxShadow: `0 18px 40px ${cardColor}33`,
            backgroundColor: '#fafafa',
            background: 'linear-gradient(160deg, #ffffff 0%, #f5f5f5 100%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.2,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            overflow: 'hidden',
          }}>
          <Typography variant="h6" sx={{ ...titleSx, textAlign: 'center' }}>
            {title}
          </Typography>

          <Box
            ref={itemsContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-around',
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehaviorY: 'contain',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: `${cardColor}4d`,
                borderRadius: 10,
              },
            }}>
            {renderItems()}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              alignItems: 'center',
              pointerEvents: 'none',
            }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                border: `1.5px solid ${cardColor}80`,
              }}
            />
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: cardColor,
              }}
            />
          </Box>
        </Box>
      </motion.div>
    </Card>
  )
}

export default ServiceCard
